// Bildschirm "Massenpflege" -- die eigenen Massenlaeufe, die Laeufe, die auf
// die eigene Freigabe warten, und EIN Lauf mit seiner Vorschau (ST-TAX-002,
// Batch-Schnitt; ADR-0058 Ziffer 13).
//
// NICHT IN DER HAUPTNAVIGATION (Experience-Review 28.09.2026, P3): eine
// seltene Aufgabe bekommt keinen Menuepunkt. Erreichbar aus der Kontaktsuche
// ("Meine Massenlaeufe") und ueber den geteilten Link.
//
// GENAU EINE HAUPTAKTION JE ZUSTAND des Laufs (NFR-UX-004):
//   * eigene Vorschau unter der Schwelle      -> "An n Kontakte vergeben" / "Bei n Kontakten entfernen"
//   * eigene Vorschau ueber der Schwelle      -> "Link kopieren" (eine zweite Person gibt frei)
//   * fremde Vorschau, die auf mich wartet    -> "Freigeben und ausführen"
//   * eigener Gegenlauf (Rueckgaengig)        -> "Rückgängig machen bestätigen"
//   * veraltet oder abgelaufen                -> "Gleiche Auswahl erneut vorlegen"
//   * ausgefuehrt                             -> keine; "Rückgängig machen" ist Zweitaktion
// Verwerfen ist immer Zweitaktion.
//
// DIE VORSCHAU ist der Hauptsatz in Worten (nur Wirkung und Konflikte); die
// Nebenzaehler und die Kontakte liegen hinter "Details" (5.2.9).

import { useCallback, useEffect, useRef, useState, type ReactElement } from 'react';
import {
  Accordion,
  AccordionHeader,
  AccordionItem,
  AccordionPanel,
  Badge,
  Body1,
  Button,
  Caption1,
  Card,
  makeStyles,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Spinner,
  Subtitle1,
  Title3,
  tokens,
} from '@fluentui/react-components';

import type { PlainMessage } from '../access.ts';
import {
  abortedMessage,
  AGAIN_ABORTED_MESSAGE,
  arrivedSentence,
  conflictSentence,
  STICHWORTKONFLIKTE_ORT,
  createBatch,
  detailSentences,
  mergeSentences,
  discardBatch,
  executeBatch,
  fetchBatch,
  fetchBatches,
  GONE_MESSAGE,
  massenlaufHref,
  previewHeadline,
  recallBatchRequest,
  rememberBatchRequest,
  hasRun,
  laufName,
  resultHeadline,
  untilPreviewEndsSentence,
  REVERSE_ABORTED_MESSAGE,
  reverseBatch,
  SECOND_PERSON_MESSAGE,
  SELECTION_GONE_MESSAGE,
  settleAfterAbort,
  statusLabel,
  UNREACHABLE_MESSAGE,
  type BatchDetail,
  type BatchSummary,
  type RepeatableCommand,
} from '../classification-batches.ts';
import { createdIssuesSentence } from '../data-quality.ts';
import { formatInstant, partyHref } from '../parties.ts';
import { AppLink, navigate } from '../shell/router.tsx';
import { Tabelle, type TabellenSpalte } from '../shell/tabelle.tsx';
import { useScreenStyles } from '../shell/screen-styles.ts';
import { VorgangNach } from '../shell/zustand.tsx';
import { meldungsIntent } from '../shell/use-section-commands.ts';
import { Brotkrume } from '../shell/brotkrume.tsx';
import { useSeitenueberschrift, useUeberschriftImInhalt } from '../shell/layout.tsx';
import { NebenKnopf } from '../shell/nebenknopf.tsx';

/**
 * Ein Satz, dessen Ort „Stichwortkonflikte“ zum Link wird -- wie auf der
 * Kontaktseite (KONTAKT #583): das Wort selbst fuehrt hin, ohne Anfuehrungszeichen.
 * Ohne `verlinkt` bleibt der Satz, wie er ist.
 */
function KonfliktSatz({ satz, verlinkt }: { readonly satz: string; readonly verlinkt: boolean }): ReactElement {
  const stelle = satz.indexOf(STICHWORTKONFLIKTE_ORT);
  if (!verlinkt || stelle < 0) return <>{satz}</>;
  return (
    <>
      {satz.slice(0, stelle)}
      <AppLink to="/datenqualitaet">Stichwortkonflikte</AppLink>
      {satz.slice(stelle + STICHWORTKONFLIKTE_ORT.length)}
    </>
  );
}

const useStyles = makeStyles({
  einstieg: { alignSelf: 'center', color: tokens.colorNeutralForeground2 },
  seitenkopf: { display: 'flex', flexDirection: 'column', gap: '0.25rem', marginBottom: '1rem' },
  titel: { margin: 0, overflowWrap: 'anywhere' },
  // "Vorschau" als Abschnittsueberschrift mit dem Zustand als Pille daneben (v4).
  vorschauKopf: { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.25rem 0.75rem', marginTop: '1rem' },
  vorschauTitel: { margin: 0 },
  // Die Kontakte im Massenlauf (Detail).
  liste: {
    listStyle: 'none',
    margin: '0.5rem 0 0',
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
  },
  hinweis: {
    display: 'block',
    marginTop: '0.75rem',
    color: tokens.colorNeutralForeground2,
  },
  abschnitt: {
    marginTop: '1.25rem',
  },
  kopf: { display: 'block', marginBottom: '0.5rem' },
  laufLink: { overflowWrap: 'anywhere' },
  satz: {
    display: 'block',
    marginTop: '0.5rem',
    overflowWrap: 'anywhere',
  },
});

export interface MassenpflegeScreenProps {
  readonly apiBaseUrl: string;
  readonly accessToken: () => Promise<string | undefined>;
  readonly onReauthenticate: () => void;
}

export const LIST_STATUS_REGION_ID = 'massenpflege-status';
export const DETAIL_STATUS_REGION_ID = 'massenlauf-status';

/**
 * Was gespeichert sein kann: die Ruecknahme und die neue Vorschau ohne Antwort, und ein
 * Abbruch, dessen Ausgang sich nicht pruefen liess. abortedMessage baut ihn je Fall neu;
 * nachAbbruch traegt ihn hier ein.
 */
const OFFENE_MELDUNGEN = new WeakSet<PlainMessage>([REVERSE_ABORTED_MESSAGE, AGAIN_ABORTED_MESSAGE]);

function Meldung({ message }: { readonly message: PlainMessage }): ReactElement {
  // Wie die Pflegeabschnitte (#709): eine Ablehnung und ein Laden ohne Ergebnis sind ein Fehler, offen warnt.
  return (
    <MessageBar intent={meldungsIntent(message, OFFENE_MELDUNGEN.has(message))}>
      <MessageBarBody>
        <MessageBarTitle>{message.headline}</MessageBarTitle>
        <div>{message.explanation}</div>
        <div>{message.nextStep}</div>
      </MessageBarBody>
    </MessageBar>
  );
}


/** Die Farbe der Status-Pille; das Wort traegt die Aussage (WCAG 1.4.1). */
function statusFarbe(batch: BatchSummary): 'success' | 'warning' | 'informative' | 'subtle' {
  if (batch.status === 'completed') return 'success';
  if (batch.status === 'reversed' || batch.status === 'discarded' || !batch.previewCurrent) return 'subtle';
  return batch.requiresSecondPerson ? 'warning' : 'informative';
}

/**
 * Bis wann der Lauf etwas erlaubt, mit dem Wort dafuer (Glossar: "Rücknahme bis <Datum>" statt
 * "Frist"): die Vorschau gilt bis, oder rueckgaengig machen geht bis. Ohne Termin leer.
 */
function terminOf(batch: BatchSummary): string {
  if (batch.status === 'previewed' && batch.previewCurrent) return `Vorschau bis ${formatInstant(batch.previewExpiresAt)}`;
  if (batch.canReverse && batch.reversibleUntil !== null) return `Rücknahme bis ${formatInstant(batch.reversibleUntil)}`;
  return '';
}

/** Die Laeufe als Tabelle (v4, shell/tabelle.tsx): Lauf, Status als Pille, Kontakte, Termin. */
function LaufTabelle({ name, laeufe }: { readonly name: string; readonly laeufe: readonly BatchSummary[] }): ReactElement {
  const { laufLink } = useStyles();
  const spalten: readonly TabellenSpalte<BatchSummary>[] = [
    {
      id: 'lauf',
      kopf: 'Lauf',
      zelle: (batch) => (
        // Der Anlagezeitpunkt steht im Linktext: Laeufe mit demselben Stichwort hiessen sonst
        // gleich (WCAG 2.4.9). Lange Stichworte ohne Leerzeichen brechen hart um.
        <AppLink to={massenlaufHref(batch.classificationChangeBatchId)} className={laufLink}>
          {laufName(batch)} · {formatInstant(batch.createdAt)}
        </AppLink>
      ),
    },
    {
      id: 'status',
      kopf: 'Status',
      zelle: (batch) => (
        <Badge appearance="tint" color={statusFarbe(batch)}>
          {statusLabel(batch)}
        </Badge>
      ),
    },
    { id: 'kontakte', kopf: 'Kontakte', zelle: (batch) => (batch.effectiveCount === 1 ? '1 Kontakt' : `${batch.effectiveCount} Kontakte`) },
    { id: 'termin', kopf: 'Bis', zelle: terminOf, stufe: 'schmal', kurz: (batch) => terminOf(batch) || undefined },
  ];
  return <Tabelle name={name} zeilen={laeufe} schluessel={(batch) => batch.classificationChangeBatchId} spalten={spalten} />;
}

/** Die Liste: "Wartet auf deine Freigabe" und "Deine letzten Massenläufe". */
export function MassenpflegeScreen({ apiBaseUrl, accessToken, onReauthenticate }: MassenpflegeScreenProps): ReactElement {
  const styles = useScreenStyles();
  const own = useStyles();
  const [status, setStatus] = useState<'laedt' | 'geladen' | 'abgelehnt'>('laedt');
  const [eigene, setEigene] = useState<readonly BatchSummary[]>([]);
  const [wartend, setWartend] = useState<readonly BatchSummary[]>([]);
  const [fehler, setFehler] = useState<PlainMessage | undefined>(undefined);
  const laufRef = useRef(0);

  const load = useCallback(async () => {
    const meinLauf = ++laufRef.current;
    setStatus('laedt');
    setFehler(undefined);
    try {
      const token = await accessToken();
      if (meinLauf !== laufRef.current) return;
      if (token === undefined) {
        onReauthenticate();
        return;
      }
      const result = await fetchBatches({ baseUrl: apiBaseUrl, accessToken: token });
      if (meinLauf !== laufRef.current) return;
      if (result.outcome === 'rejected') {
        setFehler(result.message);
        setStatus('abgelehnt');
        return;
      }
      setEigene(result.ownBatches);
      setWartend(result.awaitingApproval);
      setStatus('geladen');
    } catch {
      if (meinLauf !== laufRef.current) return;
      setFehler(UNREACHABLE_MESSAGE);
      setStatus('abgelehnt');
    }
  }, [accessToken, apiBaseUrl, onReauthenticate]);

  useEffect(() => {
    void load();
    return () => {
      laufRef.current += 1;
    };
  }, [load]);

  return (
    <Card className={styles.card}>
      {/* Ein Satz; Datenqualitaet und Stichwortkatalog erreicht man ueber die Navigation (Lueckenliste Abschnitt 9). */}
      <Body1>Ein Massenlauf vergibt oder entfernt ein Stichwort bei vielen Kontakten auf einmal.</Body1>
      {/* Der Einstieg als klare Aktion: dort auswaehlen, dann "Stichwort pflegen". */}
      <div className={styles.actions}>
        <Button
          as="a"
          href="/kontakte"
          appearance="primary"
          onClick={(ereignis) => {
            if (ereignis.button !== 0 || ereignis.metaKey || ereignis.ctrlKey || ereignis.shiftKey || ereignis.altKey) return;
            ereignis.preventDefault();
            navigate('/kontakte');
          }}
        >
          Kontakte auswählen
        </Button>
        <Caption1 className={own.einstieg}>Dann „Stichwort pflegen“.</Caption1>
      </div>
      <div aria-live="polite" role="status" aria-label="Status: Massenpflege" id={LIST_STATUS_REGION_ID} tabIndex={-1} className={own.hinweis}>
        {status === 'laedt' ? <Spinner labelPosition="after" label="Massenläufe werden geladen" /> : undefined}
        {fehler !== undefined ? <Meldung message={fehler} /> : undefined}
      </div>
      <VorgangNach meldungen={[fehler]} />
      {status === 'abgelehnt' && fehler?.retryable === true ? (
        <div className={styles.actions}>
          <Button appearance="secondary" onClick={() => void load()}>
            Erneut versuchen
          </Button>
        </div>
      ) : undefined}
      {status === 'geladen' ? (
        <>
          <section className={own.abschnitt} aria-labelledby="massenpflege-wartend">
            <Subtitle1 as="h2" id="massenpflege-wartend" className={own.kopf}>
              Wartet auf deine Freigabe
            </Subtitle1>
            {wartend.length === 0 ? (
              <Caption1 className={own.satz}>Gerade wartet nichts auf dich.</Caption1>
            ) : (
              <LaufTabelle name="Wartet auf deine Freigabe" laeufe={wartend} />
            )}
          </section>
          <section className={own.abschnitt} aria-labelledby="massenpflege-eigene">
            <Subtitle1 as="h2" id="massenpflege-eigene" className={own.kopf}>
              Deine letzten Massenläufe
            </Subtitle1>
            {eigene.length === 0 ? (
              <Caption1 className={own.satz}>Du hast noch keinen Massenlauf angelegt.</Caption1>
            ) : (
              <LaufTabelle name="Deine letzten Massenläufe" laeufe={eigene} />
            )}
          </section>
        </>
      ) : undefined}
    </Card>
  );
}

export interface MassenlaufScreenProps extends MassenpflegeScreenProps {
  readonly batchId: string;
}

type Hauptaktion = 'ausfuehren' | 'freigeben' | 'umkehr-bestaetigen' | 'link' | 'erneut' | undefined;

function hauptaktionOf(batch: BatchDetail): Hauptaktion {
  if (batch.status !== 'previewed') return undefined;
  if (!batch.previewCurrent || batch.outdated) return batch.isMine ? 'erneut' : undefined;
  if (batch.isMine && batch.isReversal && batch.canExecute) return 'umkehr-bestaetigen';
  if (batch.isMine && batch.requiresSecondPerson) return 'link';
  if (batch.canExecute) return batch.isMine ? 'ausfuehren' : 'freigeben';
  return undefined;
}

/** Die Beschriftung des Knopfs, der den Lauf ausfuehrt -- auch der Name seiner Wiederholung (WCAG 2.5.3). */
function executeLabel(batch: BatchDetail, haupt: Hauptaktion): string {
  if (haupt === 'freigeben') return 'Freigeben und ausführen';
  if (haupt === 'umkehr-bestaetigen') return 'Rückgängig machen bestätigen';
  return ausfuehrenLabel(batch);
}

function ausfuehrenLabel(batch: BatchDetail): string {
  const n = batch.effectiveCount;
  const ziel = n === 1 ? '1 Kontakt' : `${n} Kontakte`;
  if (batch.operation === 'merge_terms') return `Bei ${n === 1 ? '1 Kontakt' : `${n} Kontakten`} umhängen`;
  return batch.operation === 'assign' ? `An ${ziel} vergeben` : `Bei ${n === 1 ? '1 Kontakt' : `${n} Kontakten`} entfernen`;
}

/** Ein Massenlauf: die Vorschau in Worten, genau eine Hauptaktion, Details zugeklappt. */
export function MassenlaufScreen({ apiBaseUrl, accessToken, onReauthenticate, batchId }: MassenlaufScreenProps): ReactElement {
  const styles = useScreenStyles();
  const own = useStyles();
  const [status, setStatus] = useState<'laedt' | 'geladen' | 'weg' | 'abgelehnt'>('laedt');
  const [batch, setBatch] = useState<BatchDetail | undefined>(undefined);
  const [fehler, setFehler] = useState<PlainMessage | undefined>(undefined);
  const [erfolg, setErfolg] = useState<string | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const [umkehrFrage, setUmkehrFrage] = useState(false);
  const [auswahlWeg, setAuswahlWeg] = useState(false);
  const [faelleSatz, setFaelleSatz] = useState<string | undefined>(undefined);
  const [faelleBeiMir, setFaelleBeiMir] = useState(true);
  // Nach einem Abbruch: welcher Befehl wiederholt wird und unter welchem Namen.
  const [wiederholung, setWiederholung] = useState<{ readonly art: RepeatableCommand; readonly label: string } | undefined>(undefined);
  // Ruecknahme oder neue Vorschau blieben ohne Antwort: die Handlung ruht, bis
  // der Lauf neu geoeffnet wird -- sonst legte ein zweiter Druck den zweiten
  // offenen Lauf an, vor dem der Satz warnt.
  const [ruhend, setRuhend] = useState<'reverse' | 'again' | undefined>(undefined);
  const statusRef = useRef<HTMLDivElement | null>(null);
  const [fokus, setFokus] = useState(0);
  const laufRef = useRef(0);

  useEffect(() => {
    if (fokus > 0) statusRef.current?.focus();
  }, [fokus]);

  /**
   * Laedt den Lauf und gibt zurueck, was er fand: den Stand, `'weg'` (fuer
   * mich nicht sichtbar), `'abgeloest'` (ein neuerer Lauf oder ein anderer
   * Bildschirm gilt) oder undefined (gescheitert). `still`: nach einem
   * Befehlsabbruch -- ein Scheitern aendert die Anzeige nicht, die Meldung des
   * Befehls sagt dann, dass offen ist, ob er ankam.
   */
  const load = useCallback(async (still = false): Promise<BatchDetail | 'weg' | 'abgeloest' | undefined> => {
    const meinLauf = ++laufRef.current;
    setStatus((alt) => (alt === 'geladen' ? alt : 'laedt'));
    try {
      const token = await accessToken();
      if (meinLauf !== laufRef.current) return 'abgeloest';
      if (token === undefined) {
        onReauthenticate();
        return 'abgeloest';
      }
      const result = await fetchBatch({ baseUrl: apiBaseUrl, accessToken: token, batchId });
      if (meinLauf !== laufRef.current) return 'abgeloest';
      if (result.outcome === 'gone') {
        // Ohne Lauf auch keine Knoepfe mehr, die ihn bedienen.
        setBatch(undefined);
        setStatus('weg');
        return 'weg';
      }
      if (result.outcome === 'rejected') {
        if (!still) {
          setFehler(result.message);
          setStatus('abgelehnt');
        }
        return undefined;
      }
      setBatch(result.batch);
      setStatus('geladen');
      return result.batch;
    } catch {
      if (meinLauf !== laufRef.current) return 'abgeloest';
      if (still) return undefined;
      setFehler(UNREACHABLE_MESSAGE);
      setStatus('abgelehnt');
      return undefined;
    }
  }, [accessToken, apiBaseUrl, batchId, onReauthenticate]);

  useEffect(() => {
    setBatch(undefined);
    setErfolg(undefined);
    setFehler(undefined);
    setUmkehrFrage(false);
    setWiederholung(undefined);
    setRuhend(undefined);
    void load();
    return () => {
      laufRef.current += 1;
    };
  }, [load]);

  /** Der erste Versuch hat gewirkt; das zeigt erst der nachgeladene Stand. */
  const erfolgNachAbbruch = (art: RepeatableCommand, jetzt: BatchDetail): void => {
    setErfolg(arrivedSentence(art));
    // Die Zahl angelegter Faelle stand nur in der verlorenen Antwort (ADR-0059).
    const offen = art === 'execute' && jetzt.isMine && jetzt.conflictCount > 0;
    setFaelleSatz(offen ? `Ob dabei Stichwortkonflikte für dich entstanden sind, siehst du unter ${STICHWORTKONFLIKTE_ORT}.` : undefined);
    setFaelleBeiMir(true);
  };

  /**
   * Die Antwort fehlte: still nachladen, dann sagt der Stand, was geschah
   * (ADR-0058 Ziffer 5). Nur Ausfuehren und Verwerfen bieten die Wiederholung
   * an; Ruecknahme und neue Vorschau legten wiederholt einen zweiten Lauf an.
   */
  const nachAbbruch = async (art: 'execute' | 'discard' | 'reverse' | 'again', label: string): Promise<void> => {
    const jetzt = await load(true);
    if (jetzt === 'abgeloest') return;
    if (art === 'reverse' || art === 'again') {
      setFehler(art === 'reverse' ? REVERSE_ABORTED_MESSAGE : AGAIN_ABORTED_MESSAGE);
      setRuhend(art);
      return;
    }
    const ausgang = settleAfterAbort(art, typeof jetzt === 'string' ? 'gone' : jetzt?.status);
    if (ausgang !== 'arrived') {
      const meldung = abortedMessage(art, ausgang);
      if (ausgang === 'unknown') OFFENE_MELDUNGEN.add(meldung);
      setFehler(meldung);
      if (ausgang === 'open' || ausgang === 'unknown') setWiederholung({ art, label });
    } else if (typeof jetzt === 'object') {
      erfolgNachAbbruch(art, jetzt);
    }
  };

  /**
   * Ein Befehl; die Seite laedt danach IMMER neu -- der Server sagt, was jetzt
   * gilt. `label`: der Name des gedrueckten Knopfs, fuer "Erneut versuchen".
   * Nach einem Abbruch ist auch der urspruengliche Knopf eine Wiederholung;
   * sonst endete er bei angekommenem erstem Versuch in "schon abgeschlossen".
   */
  const befehl = async (art: 'execute' | 'discard' | 'reverse' | 'again', label = ''): Promise<void> => {
    const wiederholt = wiederholung?.art === art;
    if (batch === undefined) return;
    setBusy(true);
    setFehler(undefined);
    setErfolg(undefined);
    setWiederholung(undefined);
    try {
      const token = await accessToken();
      if (token === undefined) {
        onReauthenticate();
        return;
      }
      const optionen = { baseUrl: apiBaseUrl, accessToken: token, batchId: batch.classificationChangeBatchId };
      if (art === 'again') {
        const request = recallBatchRequest(batch.classificationChangeBatchId);
        if (request === undefined) {
          // Keine stille Weiterleitung (Experience-Review 28.09.2026, P2): der
          // Satz nennt Problem, Wirkung und naechsten Schritt, der Link fuehrt
          // in die Kontaktsuche.
          setFehler(SELECTION_GONE_MESSAGE);
          setAuswahlWeg(true);
          return;
        }
        const result = await createBatch({ baseUrl: apiBaseUrl, accessToken: token, ...request });
        if (result.outcome === 'applied') {
          rememberBatchRequest(result.classificationChangeBatchId, request);
          navigate(massenlaufHref(result.classificationChangeBatchId));
          return;
        }
        setFehler(result.message);
        return;
      }
      const result =
        art === 'execute' ? await executeBatch(optionen) : art === 'discard' ? await discardBatch(optionen) : await reverseBatch(optionen);
      if (result.outcome === 'rejected') {
        const jetzt = await load();
        if (jetzt === 'abgeloest') return;
        // Die Wiederholung trifft den Lauf, den der erste Versuch schon abschloss.
        if (wiederholt && result.reasonCode === 'classification_batch_not_open' && typeof jetzt === 'object' && settleAfterAbort(art, jetzt.status) === 'arrived') {
          erfolgNachAbbruch(art, jetzt);
          return;
        }
        setFehler(result.message);
        return;
      }
      if (art === 'reverse') {
        // Der Gegenlauf ist ein eigener Lauf mit eigener Vorschau.
        navigate(massenlaufHref(result.classificationChangeBatchId));
        return;
      }
      setErfolg(art === 'execute' ? 'Der Massenlauf ist ausgeführt.' : 'Der Massenlauf ist verworfen. Es wurde nichts geändert.');
      // Konflikte an einer einwertigen Liste sind jetzt Faelle (ADR-0059).
      setFaelleSatz(art === 'execute' ? createdIssuesSentence(result.dataQualityIssueCount, batch.isMine) : undefined);
      setFaelleBeiMir(batch.isMine);
      await load();
    } catch {
      await nachAbbruch(art, label);
    } finally {
      setBusy(false);
      setUmkehrFrage(false);
      setFokus((zahl) => zahl + 1);
    }
  };

  const linkKopieren = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}${massenlaufHref(batchId)}`);
      setErfolg('Link kopiert. Schick ihn einer Kollegin oder einem Kollegen, die freigeben darf.');
    } catch {
      setErfolg(`Kopiere diese Adresse: ${window.location.origin}${massenlaufHref(batchId)}`);
    }
    setFokus((zahl) => zahl + 1);
  };

  const haupt = batch === undefined ? undefined : hauptaktionOf(batch);
  // Der Lauf heisst wie in der Liste; solange er laedt oder fehlt, nennt die Seite nur ihre Art.
  const titel = batch === undefined ? 'Massenlauf' : laufName(batch);
  useUeberschriftImInhalt();
  useSeitenueberschrift(titel);
  // Ein verworfener Lauf legt keine Konflikte an; der Satz ueber ihren Ort waere dort falsch (Codex-Cloud-P2 #590).
  const konflikt =
    batch === undefined || batch.status === 'discarded' ? undefined : conflictSentence(batch.conflictCount, hasRun(batch), batch.isMine);
  const details = batch === undefined ? [] : detailSentences(batch);

  return (
    <>
    {/* Pfad und h1 stehen ueber der Karte im Seitenkopf, wie auf den Pflegeseiten. */}
    <div className={own.seitenkopf}>
      <Brotkrume schritte={[{ label: 'Massenpflege', to: '/massenpflege' }]} hier={titel} />
      <Title3 as="h1" className={own.titel}>
        {titel}
      </Title3>
    </div>
    <Card className={styles.card}>
      {batch?.operation === 'merge_terms' ? (
        <Body1>
          <AppLink to="/stichwortkatalog">Zum Stichwortkatalog</AppLink>
        </Body1>
      ) : undefined}
      <div aria-live="polite" role="status" aria-label="Status: Massenlauf" id={DETAIL_STATUS_REGION_ID} ref={statusRef} tabIndex={-1} className={own.hinweis}>
        {status === 'laedt' ? <Spinner labelPosition="after" label="Massenlauf wird geladen" /> : undefined}
        {status === 'weg' && fehler === undefined ? <Meldung message={GONE_MESSAGE} /> : undefined}
        {erfolg !== undefined ? (
          <MessageBar intent="success">
            <MessageBarBody>
              <div>{erfolg}</div>
              {faelleSatz !== undefined ? (
                <div>
                  <KonfliktSatz satz={faelleSatz} verlinkt={faelleBeiMir} />
                </div>
              ) : undefined}
            </MessageBarBody>
          </MessageBar>
        ) : undefined}
        {fehler !== undefined ? <Meldung message={fehler} /> : undefined}
      </div>
      <VorgangNach meldungen={[fehler]} />

      {status === 'abgelehnt' && fehler?.retryable === true ? (
        <div className={styles.actions}>
          <Button appearance="secondary" onClick={() => void load()}>
            Erneut versuchen
          </Button>
        </div>
      ) : undefined}

      {wiederholung !== undefined && status === 'geladen' ? (
        <div className={styles.actions}>
          <Button
            appearance="secondary"
            disabled={busy}
            aria-label={`Erneut versuchen: ${wiederholung.label}`}
            onClick={() => void befehl(wiederholung.art, wiederholung.label)}
          >
            Erneut versuchen
          </Button>
        </div>
      ) : undefined}


      {auswahlWeg ? (
        <Body1 className={own.satz}>
          <AppLink to="/kontakte">Zur Kontaktsuche</AppLink>
        </Body1>
      ) : undefined}

      {batch !== undefined ? (
        <>
          <div className={own.vorschauKopf}>
            <Subtitle1 as="h2" className={own.vorschauTitel}>
              {hasRun(batch) ? 'Ergebnis' : 'Vorschau'}
            </Subtitle1>
            {/* Die Pille nur, wenn sie etwas anderes sagt als die Ueberschrift (Ausgeführt, Wartet auf Freigabe ...). */}
            {statusLabel(batch) === 'Vorschau' ? undefined : (
              <Badge appearance="tint" color={statusFarbe(batch)}>
                {statusLabel(batch)}
              </Badge>
            )}
          </div>
          <Body1 className={own.satz}>{hasRun(batch) ? resultHeadline(batch) : previewHeadline(batch)}</Body1>
          {mergeSentences(batch).map((satz) => (
            <Body1 key={satz} className={own.satz}>
              {satz}
            </Body1>
          ))}
          {konflikt !== undefined ? (
            <Body1 className={own.satz}>
              {/* Verlinkt nur, wo die Faelle bei mir liegen -- und nicht doppelt zum Faelle-Satz. */}
              <KonfliktSatz satz={konflikt} verlinkt={batch.isMine && !(faelleSatz !== undefined && faelleBeiMir)} />
            </Body1>
          ) : undefined}
          {batch.status === 'previewed' && batch.previewCurrent && !batch.outdated ? (
            <Caption1 className={own.satz}>Die Vorschau gilt bis {formatInstant(batch.previewExpiresAt)}. {untilPreviewEndsSentence(batch)}</Caption1>
          ) : undefined}
          {batch.status === 'previewed' && (batch.outdated || !batch.previewCurrent) ? (
            <Caption1 className={own.satz}>
              {batch.outdated
                ? 'Seit der Vorschau hat sich an den Kontakten etwas geändert — dieser Lauf lässt sich nicht mehr ausführen.'
                : 'Diese Vorschau ist abgelaufen — dieser Lauf lässt sich nicht mehr ausführen.'}
            </Caption1>
          ) : undefined}
          {haupt === 'link' ? (
            <Caption1 className={own.satz}>
              {SECOND_PERSON_MESSAGE.explanation} {SECOND_PERSON_MESSAGE.nextStep}
            </Caption1>
          ) : undefined}
          {haupt === 'freigeben' ? (
            <Caption1 className={own.satz}>
              Eine Kollegin oder ein Kollege hat diesen Lauf vorbereitet. Mit der Freigabe führst du ihn aus und stehst als
              Ausführende im Verlauf der Kontakte.
            </Caption1>
          ) : undefined}

          <div className={styles.actions}>
            {haupt === 'ausfuehren' || haupt === 'freigeben' || haupt === 'umkehr-bestaetigen' ? (
              <Button appearance="primary" disabled={busy} onClick={() => void befehl('execute', executeLabel(batch, haupt))}>
                {executeLabel(batch, haupt)}
              </Button>
            ) : undefined}
            {haupt === 'link' ? (
              <Button appearance="primary" disabled={busy} onClick={() => void linkKopieren()}>
                Link kopieren
              </Button>
            ) : undefined}
            {haupt === 'erneut' && ruhend !== 'again' ? (
              <Button appearance="primary" disabled={busy} onClick={() => void befehl('again')}>
                Gleiche Auswahl erneut vorlegen
              </Button>
            ) : undefined}
            {batch.canDiscard ? (
              <Button appearance="secondary" disabled={busy} onClick={() => void befehl('discard', batch.isMine ? 'Verwerfen' : 'Freigabe ablehnen')}>
                {batch.isMine ? 'Verwerfen' : 'Freigabe ablehnen'}
              </Button>
            ) : undefined}
            {batch.canReverse && !umkehrFrage && ruhend !== 'reverse' ? (
              <Button appearance="secondary" disabled={busy} onClick={() => setUmkehrFrage(true)}>
                Rückgängig machen
              </Button>
            ) : undefined}
          </div>

          {umkehrFrage ? (
            <div className={own.satz}>
              {/* Folgenbezogene Bestaetigung (NFR-UX-005): was passiert und was
                  NICHT -- spaetere Einzelaenderungen bleiben. */}
              <Body1 className={own.satz}>
                Rückgängig machen nimmt nur zurück, was dieser Lauf geändert hat. Was seitdem einzeln geändert wurde, bleibt
                so. Du siehst vorher, wie viele Kontakte betroffen sind.
              </Body1>
              <div className={styles.actions}>
                <Button appearance="secondary" disabled={busy} onClick={() => void befehl('reverse')}>
                  Vorschau der Rücknahme zeigen
                </Button>
                <NebenKnopf disabled={busy} onClick={() => setUmkehrFrage(false)}>
                  Abbrechen
                </NebenKnopf>
              </div>
            </div>
          ) : undefined}

          {/* Fluents Aufklapper statt <details>: derselbe Chevron wie ueberall (L14-06). */}
          <Accordion collapsible className={own.abschnitt}>
            <AccordionItem value="details">
              <AccordionHeader>Details</AccordionHeader>
              <AccordionPanel>
                {details.map((satz) => (
                  <Caption1 key={satz} className={own.satz}>
                    {satz}
                  </Caption1>
                ))}
                <Caption1 className={own.satz}>
                  {batch.requestedCount === 1 ? '1 Kontakt' : `${batch.requestedCount} Kontakte`} ausgewählt. Die ersten, die du sehen
                  darfst:
                </Caption1>
                <ul className={own.liste} aria-label="Kontakte dieses Massenlaufs">
                  {batch.contacts.map((contact) => (
                    <li key={contact.partyId}>
                      <AppLink to={partyHref(contact.partyId)}>{contact.displayName}</AppLink>
                      {contact.outcome === 'unchanged' ? <Caption1> — bleibt unverändert</Caption1> : undefined}
                      {contact.outcome === 'not_writable' ? <Caption1> — lässt sich nicht ändern</Caption1> : undefined}
                    </li>
                  ))}
                </ul>
                {batch.additionalContactCount > 0 ? (
                  <Caption1 className={own.satz}>und {batch.additionalContactCount} weitere.</Caption1>
                ) : undefined}
              </AccordionPanel>
            </AccordionItem>
          </Accordion>
        </>
      ) : undefined}
    </Card>
    </>
  );
}
